import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UploadCloud, FolderOpen, X } from 'lucide-react';
import DocumentTable from './DocumentTable';
import DocumentGrid, { FolderTile } from './DocumentGrid';
import DocumentToolbar, { SelectionBar } from './DocumentToolbar';
import UploadDialog from './UploadDialog';
import NewFolderDialog from './NewFolderDialog';
import useDocumentActions from './useDocumentActions';
import documentService from '../../services/documentService';
import folderService from '../../services/folderService';
import useAsync from '../../hooks/useAsync';
import useSelection from '../../hooks/useSelection';
import useDisclosure from '../../hooks/useDisclosure';
import { usePreferences } from '../../context/PreferencesContext';
import { useUploads } from '../../context/UploadContext';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { TableSkeleton, GridSkeleton } from '../common/LoadingState';
import EmptyState from '../common/EmptyState';
import ConfirmDialog from '../common/ConfirmDialog';
import { P } from '../../utils/permissions';

/**
 * Shared list container behind Documents, Departments, Shared, Recent, Favourites and
 * Trash. v2 browser: folder tiles above, then file rows (list) or cards (grid).
 * Owns loading/empty/error states, selection, drag-and-drop upload and the dialogs.
 */
export default function DocumentBrowser({
  scope = 'folder',
  folderId = null,
  departmentId = null,
  header,
  variant = 'default',
  emptyState,
  showPath = false,
  allowUpload = true,
  onOpenFolder,
  reloadKey,
  onCountsChange,
}) {
  const { viewMode, setViewMode } = usePreferences();
  const { addFiles, onUploadComplete } = useUploads();
  const { can } = useAuth();
  const toast = useToast();
  const [params, setParams] = useSearchParams();

  const [sort, setSort] = useState('name:asc');
  const [typeFilter, setTypeFilter] = useState(null);
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);

  const upload = useDisclosure();
  const newFolder = useDisclosure();
  const confirmPurge = useDisclosure();

  const { data, loading, error, reload } = useAsync(
    () => documentService.listItems({ scope, folderId, departmentId, sort, typeGroup: typeFilter }),
    [scope, folderId, departmentId, sort, typeFilter, reloadKey],
  );

  const items = useMemo(() => data?.items || [], [data]);
  const folders = useMemo(() => items.filter((i) => i.type === 'folder'), [items]);
  const documents = useMemo(() => items.filter((i) => i.type !== 'folder'), [items]);
  const selection = useSelection(documents);
  const { actions, dialogs } = useDocumentActions({ reload, onOpenFolder });

  useEffect(() => {
    selection.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, folderId, departmentId]);

  useEffect(() => onUploadComplete(() => reload()), [onUploadComplete, reload]);

  useEffect(() => {
    onCountsChange?.({ folders: folders.length, documents: documents.length });
  }, [folders.length, documents.length, onCountsChange]);

  // The top bar's "Upload" / "New folder" route here with ?action=…; consume it once.
  useEffect(() => {
    const action = params.get('action');
    if (!action) return;
    const next = new URLSearchParams(params);
    next.delete('action');
    setParams(next, { replace: true });
    if (action === 'upload' && can(P.DOCUMENT_UPLOAD)) upload.open();
    if (action === 'new-folder' && can(P.FOLDER_CREATE)) newFolder.open();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const open = useCallback((item) => actions.open(item), [actions]);

  /* ------------------------------------------------------------ drag & drop */
  const canUploadHere = allowUpload && scope === 'folder' && can(P.DOCUMENT_UPLOAD);

  const onDragEnter = (e) => {
    if (!canUploadHere || !e.dataTransfer?.types?.includes('Files')) return;
    dragDepth.current += 1;
    setDragging(true);
  };
  const onDragLeave = () => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  };
  const onDrop = (e) => {
    if (!canUploadHere) return;
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    const result = addFiles(e.dataTransfer.files, folderId);
    if (result.rejected.length) toast.error(`${result.rejected.length} file(s) rejected: ${result.rejected[0].reason}`);
  };

  /* ----------------------------------------------------------- bulk actions */
  const selected = selection.selectedItems;

  // Trash is a different verb set, not the same one applied to deleted rows. A deleted
  // document cannot be downloaded, moved, shared or deleted again — every route for those
  // resolves the document with `deleted_at IS NULL` and answers 404. Offering them was the
  // bug: the bar rendered Download, Move and Delete, and all three said "Document not found".
  const inTrash = variant === 'trash' || scope === 'trash';

  const bulk = inTrash
    ? {
        canRestore: can(P.DOCUMENT_DELETE) && selected.every((i) => i.capabilities?.includes('delete')),
        canPurge: can(P.DOCUMENT_DELETE_PERMANENT),
      }
    : {
        canDownload: can(P.DOCUMENT_DOWNLOAD) && selected.every((i) => i.capabilities?.includes('download')),
        canMove: can(P.FOLDER_MOVE) && selected.every((i) => i.capabilities?.includes('edit')),
        canShare: can(P.DOCUMENT_SHARE) && selected.length === 1 && selected[0].capabilities?.includes('share'),
        canDelete: can(P.DOCUMENT_DELETE) && selected.every((i) => i.capabilities?.includes('delete')),
      };

  const bulkDelete = async () => {
    try {
      await Promise.all(selected.map((i) => (i.type === 'folder' ? folderService.remove(i.id) : documentService.remove(i.id))));
      toast.success(`${selected.length} item(s) moved to Trash`);
      selection.clear();
      reload();
    } catch (err) {
      toast.error(err.message || 'Some items could not be deleted.');
    }
  };

  const bulkRestore = async () => {
    // Settled rather than all: one document whose original folder has since been deleted
    // should not stop the others coming back.
    const results = await Promise.allSettled(selected.map((i) => documentService.restore(i.id)));
    const failed = results.filter((r) => r.status === 'rejected');
    if (failed.length) {
      toast.error(
        failed.length === selected.length
          ? failed[0].reason?.message || 'Those documents could not be restored.'
          : `${selected.length - failed.length} restored, ${failed.length} could not be.`,
      );
    } else {
      toast.success(`${selected.length} document${selected.length === 1 ? '' : 's'} restored`);
    }
    selection.clear();
    reload();
  };

  const bulkPurge = async () => {
    const results = await Promise.allSettled(selected.map((i) => documentService.purge(i.id)));
    const failed = results.filter((r) => r.status === 'rejected');
    if (failed.length) {
      // The commonest refusal here is a legal hold, and its message says so. Surfacing it
      // verbatim is the difference between "it did not work" and knowing why.
      toast.error(failed[0].reason?.message || `${failed.length} document(s) could not be deleted.`);
    } else {
      toast.success(`${selected.length} document${selected.length === 1 ? '' : 's'} permanently deleted`);
    }
    selection.clear();
    reload();
  };

  /* ------------------------------------------------------------------ render */
  const isEmpty = !loading && items.length === 0;

  const body = () => {
    if (error) {
      return (
        <div className="banner banner--danger">
          <X size={16} />
          <div className="stack" style={{ gap: 4 }}>
            <strong>{error.status === 403 ? 'Access restricted' : 'Could not load this view'}</strong>
            <span>{error.message}</span>
            <button type="button" className="link" style={{ alignSelf: 'flex-start', paddingLeft: 0 }} onClick={reload}>
              Try again
            </button>
          </div>
        </div>
      );
    }

    if (loading && !data) return viewMode === 'grid' ? <GridSkeleton /> : <TableSkeleton />;

    if (isEmpty) {
      if (typeFilter) {
        return (
          <div className="panel">
            <EmptyState
              title="Nothing matches that filter"
              body="Clear the file-type filter to see everything in this view."
              action={{ label: 'Clear filter', onClick: () => setTypeFilter(null) }}
            />
          </div>
        );
      }
      return (
        <div className="panel">
          <EmptyState
            icon={emptyState?.icon || FolderOpen}
            title={emptyState?.title || 'No documents yet'}
            body={emptyState?.body || 'Upload your first document to get started.'}
            action={
              emptyState?.action ||
              (canUploadHere ? { label: 'Upload document', icon: UploadCloud, onClick: () => upload.open() } : undefined)
            }
          />
        </div>
      );
    }

    if (viewMode === 'grid') return <DocumentGrid items={items} selection={selection} onOpen={open} actions={actions} />;

    return (
      <>
        {folders.length > 0 && (
          <div className="grid-folders">
            {folders.map((f) => (
              <FolderTile key={f.id} item={f} onOpen={open} actions={actions} />
            ))}
          </div>
        )}
        {documents.length > 0 && (
          <DocumentTable items={documents} selection={selection} onOpen={open} actions={actions} variant={variant} showPath={showPath} />
        )}
      </>
    );
  };

  return (
    <section
      className="screen"
      style={{ position: 'relative' }}
      onDragEnter={onDragEnter}
      onDragOver={(e) => canUploadHere && e.preventDefault()}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {typeof header === 'function' ? header({ folders: folders.length, documents: documents.length }) : header}

      {selection.count > 0 ? (
        <SelectionBar
          count={selection.count}
          onClear={selection.clear}
          onDownload={() => selected.forEach((i) => actions.download(i))}
          onMove={() => actions.move(selected[0])}
          onShare={() => actions.share(selected[0])}
          onDelete={bulkDelete}
          onRestore={bulkRestore}
          onPurge={confirmPurge.open}
          {...bulk}
        />
      ) : (
        <DocumentToolbar
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          sort={sort}
          onSortChange={setSort}
          typeFilter={typeFilter}
          onTypeFilterChange={setTypeFilter}
        />
      )}

      {body()}

      {dragging && (
        <div className="drop-overlay">
          <div className="drop-overlay__inner">
            <UploadCloud size={30} />
            <span>Drop files to upload here</span>
          </div>
        </div>
      )}

      <UploadDialog open={upload.isOpen} onClose={upload.close} folderId={folderId} folderName={emptyState?.folderName || 'this folder'} />

      <NewFolderDialog
        open={newFolder.isOpen}
        onClose={newFolder.close}
        onSubmit={async (name) => {
          await folderService.create({ name, parentId: folderId, departmentId });
          toast.success(`Folder "${name}" created`);
          reload();
        }}
      />

      <ConfirmDialog
        open={confirmPurge.isOpen}
        onClose={confirmPurge.close}
        onConfirm={bulkPurge}
        destructive
        title={`Permanently delete ${selection.count} document${selection.count === 1 ? '' : 's'}?`}
        message="The files and every version of them will be erased. This cannot be undone."
        detail="A record of the deletion is kept in the audit trail. Documents under a legal hold will be refused."
        confirmLabel="Delete permanently"
      />

      {dialogs}
    </section>
  );
}
